import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaTimes, FaCar, FaEdit, FaExpand, FaImage, FaClipboard, FaProjectDiagram, FaMoneyBill, FaInfoCircle, FaSearchLocation, FaRulerCombined, FaTag, FaBalanceScale, FaList, FaStore, FaWhatsapp, FaEye, FaEyeSlash, FaBan } from 'react-icons/fa';
import { getPriorityClasses, getStatusClasses } from '../../../utils/LabelColors';
import ConteoCheck from '../../../components/productos/ConteoCheck';
import RotacionBadge from '../../../components/productos/RotacionBadge';
import CrossSellPanel from '../../../components/productos/CrossSellPanel';
import WhatsAppShareModal from '../../../components/Dashboard/Welcome/WhatsAppShareModal';
import { storeProductUrl } from '../../../utils/storeConstants';
import AuditoriaProducto from './AuditoriaProducto';
import TabsTransaccionesProducto from './Transacciones/TabsTransaccionesProducto';
import ModalProductoPadre from '../../../components/modals/ModalProductoPadre';
import { validarUbicacion } from '../../../components/ubicacionValidator';
import { useMediaQuery } from 'react-responsive';
import TablaVariantes from '../../../components/productos/TablaVariantes';
import LoadingAnimation from '../../../components/anims/LoadingAnimation';
import CompatibilityCheckModal from './CompatibilityCheckModal';
import SolicitudInternaButton from '../../../components/productos/SolicitudInternaButton';
import { usePermissions } from '../../../context/PermissionsContext';

// Clases de estilo compartidas
const cardStyle = "bg-white p-6 rounded-xl shadow-lg border border-gray-100";
const labelStyle = "text-sm font-semibold text-gray-500 uppercase tracking-wider";
const valueStyle = "text-xl font-bold text-gray-800";
const iconButtonStyle = "text-white bg-indigo-500 rounded-full w-11 h-11 flex items-center justify-center shadow-lg hover:bg-indigo-600 transition duration-200 transform hover:scale-105";


const VistaProducto = ({ token, id_producto }) => {
    // Si id_producto viene como prop, se usa. Si no, se usa el de la URL.
    const { id_producto: id_from_url } = useParams();
    const productId = id_producto || id_from_url;
    const navigate = useNavigate();
    const [producto, setProducto] = useState({});
    const [modalImage, setModalImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isAuditoriaOpen, setIsAuditoriaOpen] = useState(false);
    const [isVentasOpen, setIsVentasOpen] = useState(false);
    const [isModalProductoPadreOpen, setIsModalProductoPadreOpen] = useState(false);
    const [productosHijos, setProductosHijos] = useState([]);
    const [shareProduct, setShareProduct] = useState(null);
    const [mostrarInfoInterna, setMostrarInfoInterna] = useState(false);
    const [isCompatibilityOpen, setIsCompatibilityOpen] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const { hasPermission } = usePermissions();
    const canVerLiquidacionAuto = hasPermission('productos.liquidacion_auto');
    const productoDesactivado = Number(producto.es_activo) === 0;

    const fetchData = async () => {
        try {
            const response = await axios.get(`/api/productos/editar/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data;

            const precioVentaConIva = data.id_impuesto === 5
                ? parseFloat(data.precio_venta * 1.19).toFixed(2)
                : parseFloat(data.precio_venta).toFixed(2);

            // Si es padre, los precios min/max ya vienen con impuestos del backend
            setProducto({
                ...data,
                precio_venta: precioVentaConIva
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [productId, token]);

    // Efecto para cargar hijos si es padre
    useEffect(() => {
        const fetchHijos = async () => {
            if (producto.es_padre) {
                try {
                    const response = await axios.get(`/api/productos/hijos/${productId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setProductosHijos(response.data);
                } catch (error) {
                    console.error("Error cargando variantes:", error);
                }
            }
        };
        if (producto.id_producto) fetchHijos();
    }, [producto.es_padre, producto.id_producto, productId, token]);

    // Lógica para determinar qué descripción mostrar
    const descripcionToShow = producto.descripcion_meli
        ? producto.descripcion_meli
        : producto.nombre_producto; // Asumo que el nombre del producto es la descripción si no hay otra

    // Filtrar atributos que tengan valor (no null, no undefined, no vacíos)
    const validAttributes = producto.atributos_especificos?.filter(
        attr => attr.valor !== null && attr.valor !== undefined && String(attr.valor).trim() !== ''
    ) || [];

    if (loading) return <LoadingAnimation />;

    const openModal = () => {
        if (producto.imagenes && producto.imagenes.length > 0) {
            setModalImage(producto.imagenes[currentImageIndex]?.url);
        }
    };

    const closeModal = () => {
        setModalImage(null);
    };

    const handleNextImage = () => {
        if (!producto.imagenes || producto.imagenes.length === 0) return;
        const nextIndex = (currentImageIndex + 1) % producto.imagenes.length;
        setCurrentImageIndex(nextIndex);
        setModalImage(producto.imagenes[nextIndex].url);
    };

    const handlePreviousImage = () => {
        if (!producto.imagenes || producto.imagenes.length === 0) return;
        const prevIndex = (currentImageIndex - 1 + producto.imagenes.length) % producto.imagenes.length;
        setCurrentImageIndex(prevIndex);
        setModalImage(producto.imagenes[prevIndex].url);
    };

    const handleDotClick = (index) => {
        setCurrentImageIndex(index);
        // Si el modal está abierto, actualiza la imagen también
        if (modalImage) setModalImage(producto.imagenes[index].url);
    };

    // Función para determinar el estilo del precio de venta
    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(price);
    };

    const renderPrecioCompra = () => {
        if (producto.es_padre && producto.precio_compra_min != null && producto.precio_compra_max != null) {
            return producto.precio_compra_min === producto.precio_compra_max
                ? formatPrice(producto.precio_compra_min)
                : `${formatPrice(producto.precio_compra_min)} - ${formatPrice(producto.precio_compra_max)}`;
        }
        if (producto.precio_compra != null && producto.precio_compra !== '') {
            return formatPrice(producto.precio_compra);
        }
        return 'N/A';
    };

    const handleOpenMeli = async () => {
        if (producto.enlace_meli) {
            window.open(producto.enlace_meli, '_blank');
            return;
        }
        try {
            const res = await axios.get(`/api/productos/meli-link/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.status === 'success') {
                window.open(res.data.enlace_meli, '_blank');
            } else {
                const { default: Swal } = await import('sweetalert2');
                Swal.fire({
                    icon: 'info',
                    title: 'No Publicado',
                    text: 'Este producto no tiene una publicación activa en MercadoLibre.',
                    confirmButtonColor: '#ffc240',
                    confirmButtonText: 'Entendido'
                });
            }
        } catch (err) {
            console.error('Error fetching meli link:', err);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto bg-white shadow-2xl rounded-xl overflow-hidden relative">

                {/* Encabezado y botones de acción */}
                <div className="flex justify-between items-start bg-gradient-to-r from-official-from via-official-via to-official-to 90% p-4 sm:p-6 text-white shadow-md">
                    <p className="text-xl sm:text-2xl font-semibold max-w-[70%]">
                        {producto.es_hijo === false && producto.es_padre === false && 'Producto Simple'}
                        {producto.es_hijo === true && producto.es_padre === false && 'Producto Variante: Hijo'}
                        {producto.es_hijo === false && producto.es_padre === true && 'Producto Variante: Padre'}
                    </p>
                    {/* Contenedor de botones */}
                    <div className="flex space-x-3 absolute top-4 right-4 z-10">
                        {/* Botón Volver al Padre (Solo Hijo) */}
                        {producto.es_hijo && producto.id_padre && (
                            <button
                                onClick={() => navigate(`/productos/vista/${producto.id_padre}`)}
                                className={iconButtonStyle.replace('indigo', 'gray')}
                                title="Volver al Producto Padre"
                            >
                                <FaChevronLeft className="text-xl" />
                            </button>
                        )}
                        {/* Botón de variantes */}
                        {producto.es_hijo === false && producto.es_padre === true && (
                            <button
                                onClick={() => setIsModalProductoPadreOpen(true)}
                                className={iconButtonStyle.replace('indigo', 'sky')}
                                title="Ver Variantes"
                            >
                                <FaProjectDiagram className="text-xl" />
                            </button>
                        )}
                        {/* Botón de Ventas */}
                        <button
                            onClick={() => setIsVentasOpen(true)}
                            className={iconButtonStyle.replace('indigo', 'green')}
                            title="Ventas"
                        >
                            <FaMoneyBill className="text-xl" />
                        </button>
                        {/* Botón de inspección */}
                        <button
                            onClick={() => setIsAuditoriaOpen(true)}
                            className={iconButtonStyle.replace('indigo', 'yellow')}
                            title="Auditoría"
                        >
                            <FaClipboard className="text-xl" />
                        </button>
                        {/* Botón de editar */}
                        <button
                            onClick={() => navigate(`/productos/editar/${productId}`)}
                            className={iconButtonStyle.replace('indigo', 'red')}
                            title="Editar Producto"
                        >
                            <FaEdit className="text-xl" />
                        </button>
                    </div>
                </div>

                {productoDesactivado && (
                    <div className="mx-4 sm:mx-6 mt-4 mb-0 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-900">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                            <FaBan className="mt-0.5 shrink-0 text-red-600" />
                            <div className="min-w-0">
                                <p className="font-bold text-sm sm:text-base">Producto desactivado</p>
                                <p className="text-xs sm:text-sm text-red-800/90">
                                    No publicado / no vendible en canales internos activos. Actívalo en Editar si corresponde.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => navigate(`/productos/editar/${productId}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
                            >
                                <FaEdit /> Ir a Editar
                            </button>
                            {canVerLiquidacionAuto && (
                                <Link
                                    to="/productos/liquidacion-auto"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-800 text-xs font-semibold hover:bg-red-100"
                                >
                                    Auto-desactivados
                                </Link>
                            )}
                        </div>
                    </div>
                )}

                <div className='p-6 sm:p-8'>
                    {/* Título, toggle interno y Conteo Check */}
                    <div className="flex justify-between items-start mb-6 border-b pb-4 gap-4">
                        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                            <h2 className="text-3xl font-extrabold text-gray-800">
                                {producto.nombre_producto}
                            </h2>
                            {productoDesactivado && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                    Desactivado
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => setMostrarInfoInterna((v) => !v)}
                                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors ${
                                    mostrarInfoInterna
                                        ? 'text-indigo-700 border-indigo-200 bg-indigo-50'
                                        : 'text-gray-500 border-gray-200 hover:text-indigo-600 hover:border-indigo-200'
                                }`}
                                title={mostrarInfoInterna ? 'Ocultar datos internos' : 'Mostrar proveedor y precio compra'}
                            >
                                {mostrarInfoInterna ? <FaEyeSlash /> : <FaEye />}
                                {mostrarInfoInterna ? 'Ocultar' : 'Mostrar'}
                            </button>
                            <div
                                className="flex items-center gap-2"
                                title="Rotación: solo para empleado — pase el cursor sobre el ícono"
                            >
                                <RotacionBadge
                                    estadoRotacion={producto.estado_rotacion}
                                    ventas30={producto.ventas_30_dias}
                                    fechaUltimaVenta={producto.fecha_ultima_venta}
                                    tooltipId={`rot-vista-${productId}`}
                                />
                                <ConteoCheck
                                    show={producto.tieneMovimientosRecientes}
                                    empleado={producto.nombre_empleado}
                                    fecha={producto.fecha_ultimo_movimiento}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Solicitudes internas a bodega */}
                    <div className="mb-4">
                        <SolicitudInternaButton
                            token={token}
                            idProducto={productId}
                            esPadre={Boolean(producto.es_padre)}
                            nombreProducto={producto.nombre_producto}
                            variant="buttons"
                        />
                    </div>

                    {/* Enlaces externos: Tienda, MercadoLibre, WhatsApp */}
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-6">
                        <a
                            href={storeProductUrl(productId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-5 py-3 rounded-xl hover:bg-gray-800 transition duration-200 font-bold shadow-md"
                        >
                            <FaStore className="text-lg" />
                            Ver en Tienda Virtual
                        </a>
                        <button
                            type="button"
                            onClick={handleOpenMeli}
                            className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 bg-white border-2 border-yellow-500 text-gray-900 px-5 py-3 rounded-xl hover:bg-yellow-50 transition duration-200 font-bold shadow-md"
                        >
                            <img
                                src="/images/mercado-libre.png"
                                alt="MercadoLibre"
                                className="w-5 h-5 object-contain"
                            />
                            Ver en MercadoLibre
                        </button>
                        <button
                            type="button"
                            onClick={() => setShareProduct(producto)}
                            className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 bg-green-500 text-white px-5 py-3 rounded-xl hover:bg-green-600 transition duration-200 font-bold shadow-md"
                        >
                            <FaWhatsapp className="text-lg" />
                            Compartir WhatsApp
                        </button>
                    </div>


                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Columna 1: Galería de Imágenes */}
                        <div className="lg:col-span-1 flex flex-col items-center">
                            {producto.imagenes && producto.imagenes.length > 0 ? (
                                <div className="relative w-full aspect-square max-w-sm rounded-xl overflow-hidden shadow-xl border-4 border-gray-200 group">
                                    <img
                                        src={producto.imagenes[currentImageIndex].url}
                                        alt={`Imagen ${currentImageIndex + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Badge de Origen de Imagen (Padre/Hijo) - Informativo */}
                                    {producto.imagenes[currentImageIndex].origen === 'Hijo' && (
                                        <div className="absolute top-2 right-2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10 flex items-center">
                                            <FaProjectDiagram className="mr-1" />
                                            {producto.imagenes[currentImageIndex].nombre_hijo ? `Variante: ${producto.imagenes[currentImageIndex].nombre_hijo}` : 'Variante'}
                                        </div>
                                    )}
                                    <div
                                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer bg-black bg-opacity-10 hover:bg-opacity-20"
                                        onClick={openModal}
                                    >
                                        <div className="bg-yellow-500 rounded-full p-3 shadow-xl">
                                            <FaExpand className="text-white text-3xl" />
                                        </div>
                                    </div>
                                    {/* Botones de navegación de imagen */}
                                    <button
                                        onClick={handlePreviousImage}
                                        className="absolute left-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition"
                                        title="Anterior"
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <button
                                        onClick={handleNextImage}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition"
                                        title="Siguiente"
                                    >
                                        <FaChevronRight />
                                    </button>

                                </div>
                            ) : (
                                <div className="w-full aspect-square max-w-sm bg-gray-100 flex flex-col items-center justify-center rounded-xl border-4 border-dashed border-gray-300">
                                    <FaImage className="text-gray-400 text-6xl mb-3" />
                                    <p className="text-gray-500 font-medium">Sin imágenes disponibles</p>
                                </div>
                            )}

                            {/* Dots de paginación */}
                            {producto.imagenes && producto.imagenes.length > 1 && (
                                <div className="mt-4 flex space-x-2">
                                    {producto.imagenes.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`w-3 h-3 rounded-full transition-colors ${index === currentImageIndex ? 'bg-indigo-600 shadow-md' : 'bg-gray-300 hover:bg-gray-400'}`}
                                            onClick={() => handleDotClick(index)}
                                            title={`Imagen ${index + 1}`}
                                        ></button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Columna 2 y 3: Información y Clasificación */}
                        <div className="lg:col-span-2 space-y-6">

                            {/* Fila de SKU, Código y Ubicación */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* SKU */}
                                <div className={cardStyle}>
                                    <p className={labelStyle}>SKU</p>
                                    <h3 className={`${valueStyle} text-indigo-700`}>{producto.sku}</h3>
                                </div>
                                {/* Código de Barras */}
                                <div className={cardStyle}>
                                    <p className={labelStyle}>Código de Barras</p>
                                    <p className={valueStyle}>{producto.codigo_barras || 'N/A'}</p>
                                </div>
                                {/* Ubicación */}
                                <div className={cardStyle}>
                                    <p className={labelStyle}><FaSearchLocation className='inline mr-1' /> Ubicación</p>
                                    <p className={`${valueStyle} text-red-600`}>{validarUbicacion(producto.ubicacion)}</p>
                                </div>
                            </div>

                            {/* Fila de Métricas Dinámicas (Precios, Existencias, Stock, Yhat, Medidas, Homonimos) */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {/* Precio de Venta (Siempre) */}
                                <div className={`${cardStyle} bg-green-50`}>
                                    <p className={labelStyle}>Precio Venta</p>
                                    <p className={`${valueStyle} text-green-600`}>
                                        {producto.es_padre && producto.precio_venta_min && producto.precio_venta_max
                                            ? (
                                                producto.precio_venta_min === producto.precio_venta_max
                                                    ? formatPrice(producto.precio_venta_min)
                                                    : `${formatPrice(producto.precio_venta_min)} - ${formatPrice(producto.precio_venta_max)}`
                                            )
                                            : formatPrice(producto.precio_venta)
                                        }
                                    </p>
                                </div>
                                {/* Existencias (Siempre) */}
                                <div className={`${cardStyle} bg-yellow-50`}>
                                    <p className={labelStyle}>Existencias</p>
                                    <p className={`${valueStyle} text-yellow-700`}>{parseFloat(producto.existencias).toFixed(1)}</p>
                                </div>
                                {/* Stock Mínimo (Siempre) */}
                                <div className={cardStyle}>
                                    <p className={labelStyle}>Stock Mínimo</p>
                                    <p className={valueStyle}>{producto.stock_minimo}</p>
                                </div>

                                {/* Precio compra (sin IVA) — solo con toggle */}
                                {mostrarInfoInterna && (
                                    <div className={`${cardStyle} bg-purple-50`}>
                                        <p className={labelStyle}>Precio compra (sin IVA)</p>
                                        <p className={`${valueStyle} text-purple-700`}>
                                            {renderPrecioCompra()}
                                        </p>
                                    </div>
                                )}

                                {/* Estimación (Yhat) - CONDICIONAL */}
                                {producto.yhat && parseFloat(producto.yhat) !== 0 && (
                                    <div className={`${cardStyle} bg-sky-50`}>
                                        <p className={labelStyle}><FaBalanceScale className='inline mr-1' /> Estimación (Yhat)</p>
                                        <p className={`${valueStyle} text-sky-600`}>
                                            {parseFloat(producto.yhat).toFixed(1)}
                                        </p>
                                    </div>
                                )}
                                {/* Medidas - CONDICIONAL */}
                                {producto.medidas && (
                                    <div className={cardStyle}>
                                        <p className={labelStyle}><FaRulerCombined className='inline mr-1' /> Medidas</p>
                                        <p className='text-lg font-medium'>{producto.medidas}</p>
                                    </div>
                                )}
                                {/* Homonimos - CONDICIONAL */}
                                {producto.homonimos && (
                                    <div className={cardStyle}>
                                        <p className={labelStyle}>Homonimos</p>
                                        <p className='text-lg font-medium'>{producto.homonimos}</p>
                                    </div>
                                )}
                            </div>

                            {/* NUEVO: Sección de Atributos Personalizados (Especificaciones Técnicas) */}
                            {validAttributes.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-700 mb-3 flex items-center">
                                        <FaList className="mr-2 text-orange-500" /> Especificaciones Técnicas
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {validAttributes.map((attr, index) => (
                                            <div key={index} className="bg-orange-50 p-4 rounded-lg border border-orange-100 shadow-sm">
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{attr.nombre_atributo}</p>
                                                <p className="text-gray-900 font-bold break-words text-md">
                                                    {attr.valor}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sección de aplicaciones del vehículo + CTA IA */}
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                                    <h3 className="text-lg font-bold text-gray-700 flex items-center">
                                        <FaCar className="mr-2 text-sky-500" /> Aplicaciones (Vehículos compatibles)
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={() => setIsCompatibilityOpen(true)}
                                        className="inline-flex items-center justify-center gap-2 self-start rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 shadow-sm"
                                    >
                                        <FaCar /> Probar con IA
                                    </button>
                                </div>
                                {producto.aplicaciones && producto.aplicaciones.length > 0 ? (
                                    <div className="flex flex-wrap gap-3 p-4 bg-sky-50 rounded-lg border border-sky-200">
                                        {producto.aplicaciones.map((app, index) => (
                                            <span
                                                key={index}
                                                className="bg-sky-200 text-sky-900 px-3 py-1 rounded-full text-sm font-semibold shadow-sm"
                                            >
                                                {app.vehiculo_completo}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-4">
                                        Sin aplicaciones internas registradas. Usa la validación con IA para probar compatibilidad.
                                    </p>
                                )}
                            </div>

                            {/* Bloque de Clasificación y Proveedor */}
                            <div className={cardStyle}>
                                <h4 className='text-lg font-bold text-gray-700 mb-3 flex items-center border-b pb-2'>
                                    <FaTag className='mr-2 text-red-500' />
                                    {mostrarInfoInterna ? 'Clasificación y Proveedor' : 'Clasificación'}
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {/* Prioridad (CENTRADO Y MÁS ANGOSTO) */}
                                    <div className='flex flex-col items-center text-center'>
                                        <p className={labelStyle}>Prioridad</p>
                                        <span className={`${getPriorityClasses(producto.prioridad)} px-2 py-1 text-xs font-bold rounded-full w-20 inline-block mt-1`}>
                                            {producto.prioridad || 'N/A'}
                                        </span>
                                    </div>
                                    {/* Estado (CENTRADO Y MÁS ANGOSTO) */}
                                    <div className='flex flex-col items-center text-center'>
                                        <p className={labelStyle}>Estado</p>
                                        <span className={`${getStatusClasses(producto.estado)} px-2 py-1 text-xs font-bold rounded-full w-20 inline-block mt-1`}>
                                            {producto.estado || 'N/A'}
                                        </span>
                                    </div>
                                    <div className='flex flex-col'>
                                        <p className={labelStyle}>Marca</p>
                                        <p className='text-lg font-semibold'>{producto.marca || 'N/A'}</p>
                                    </div>
                                    <div className='flex flex-col'>
                                        <p className={labelStyle}>Categoría</p>
                                        <p className='text-lg font-semibold'>{producto.categoria || 'N/A'}</p>
                                    </div>
                                    {mostrarInfoInterna && (
                                        <div className='flex flex-col col-span-4 mt-2'>
                                            <p className={labelStyle}>Proveedor</p>
                                            <p className='text-lg font-semibold'>{producto.nombre_proveedor || 'N/A'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>

                    <CrossSellPanel productId={productId} token={token} />

                    {/* Tabla de Variantes (Solo si es Padre) */}
                    {producto.es_padre && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                                <FaProjectDiagram className="mr-2 text-indigo-600" /> Variantes del Producto
                            </h3>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <TablaVariantes
                                    productosHijos={productosHijos}
                                    setProductosHijos={setProductosHijos}
                                    token={token}
                                    isMobile={isMobile}
                                />
                            </div>
                        </div>
                    )}

                    {/* Sección de Descripción (NUEVA) */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                        <h3 className="text-2xl font-bold text-gray-700 mb-4 flex items-center border-b pb-2">
                            <FaInfoCircle className="mr-2 text-indigo-600" /> Descripción del Producto
                            {producto.descripcion_meli && <span className='ml-2 text-sm text-gray-500'>(Usando descripción de MELI)</span>}
                        </h3>
                        <div className='bg-gray-50 p-4 rounded-lg shadow-inner'>
                            {descripcionToShow ? (
                                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                                    {descripcionToShow}
                                </p>
                            ) : (
                                <p className="text-gray-500 italic">No hay una descripción detallada disponible para este producto.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal de imágenes (Actualizado con centrado y mejores estilos) */}
                {modalImage && (
                    <div
                        className="fixed inset-0 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                        onClick={closeModal}
                    >
                        <div
                            className="relative max-w-2xl max-h-[70vh] flex justify-center items-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={closeModal}
                                className="absolute top-2 right-2 text-white text-3xl font-bold bg-gray-800 bg-opacity-70 p-2 rounded-full hover:bg-red-600 transition duration-200 transform hover:scale-110"
                            >
                                <FaTimes />
                            </button>
                            {/* Botones de navegación dentro del modal */}
                            {producto.imagenes && producto.imagenes.length > 1 && (
                                <>
                                    <button
                                        onClick={handlePreviousImage}
                                        className="absolute left-[-50px] top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-yellow-500 text-white text-xl hover:bg-black/70 transition hidden sm:block"
                                        title="Anterior"
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <button
                                        onClick={handleNextImage}
                                        className="absolute right-[-50px] top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-yellow-500 text-white text-xl hover:bg-black/70 transition hidden sm:block"
                                        title="Siguiente"
                                    >
                                        <FaChevronRight />
                                    </button>
                                </>
                            )}
                            <img
                                src={modalImage}
                                alt="Imagen ampliada"
                                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl border-4 border-white"
                            />
                            {/* Badge en Modal (Centrado Abajo) - Informativo */}
                            {producto.imagenes && producto.imagenes[currentImageIndex]?.origen === 'Hijo' && (
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white text-base font-bold px-6 py-2 rounded-full shadow-lg z-50 flex items-center whitespace-nowrap pointer-events-none">
                                    <FaProjectDiagram className="mr-2" />
                                    {producto.imagenes[currentImageIndex].nombre_hijo ? `Variante: ${producto.imagenes[currentImageIndex].nombre_hijo}` : 'Variante'}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Modal de auditoría */}
                {isAuditoriaOpen && (
                    <div className="fixed inset-0 bg-black-500 bg-opacity-70 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-4xl relative overflow-y-auto max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}>
                            <button
                                onClick={() => setIsAuditoriaOpen(false)}
                                className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 text-2xl"
                            >
                                <FaTimes />
                            </button>
                            <h2 className="text-2xl font-bold mb-4 border-b pb-2">Auditoría del Producto</h2>
                            <AuditoriaProducto id_producto={productId} token={token} />
                        </div>
                    </div>
                )}

                {/* Modal de Ventas */}
                {isVentasOpen && (
                    <div className="fixed inset-0 bg-black-500 bg-opacity-70 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-5xl relative overflow-y-auto max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}>
                            <button
                                onClick={() => setIsVentasOpen(false)}
                                className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 text-2xl"
                            >
                                <FaTimes />
                            </button>
                            <h2 className="text-2xl font-bold mb-4 text-center border-b pb-2">Transacciones del Producto</h2>
                            <TabsTransaccionesProducto id_producto={productId} es_padre={producto.es_padre} token={token} />
                        </div>
                    </div>
                )}

                {/* Modal Producto Padre */}
                {isModalProductoPadreOpen && (
                    <ModalProductoPadre
                        productoPadre={producto}
                        onClose={() => setIsModalProductoPadreOpen(false)}
                        token={token}
                    />
                )}

                {shareProduct && (
                    <WhatsAppShareModal
                        producto={shareProduct}
                        onClose={() => setShareProduct(null)}
                        token={token}
                    />
                )}

                <CompatibilityCheckModal
                    isOpen={isCompatibilityOpen}
                    onClose={() => setIsCompatibilityOpen(false)}
                    token={token}
                    producto={producto}
                    productosHijos={productosHijos}
                />
            </div>
        </div>
    );
};

export default VistaProducto;